"use client";

import type * as React from "react";

import { cn } from "./utils";

function Table({
  className,
  containerClassName,
  ...props
}: React.ComponentProps<"table"> & {
  /**
   * Styles the scroll container rather than the `<table>`. Bounding its height
   * here is what turns a sticky header row into a header that actually stays
   * put: `position: sticky` resolves against the nearest scrollport, and this
   * element is it.
   */
  containerClassName?: string;
}) {
  return (
    <div
      className={cn("relative w-full overflow-x-auto", containerClassName)}
      data-slot="table-container"
    >
      <table
        className={cn("w-full caption-bottom text-sm", className)}
        data-slot="table"
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return <thead className={cn("[&_tr]:border-b", className)} data-slot="table-header" {...props} />;
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      className={cn("[&_tr:last-child]:border-0", className)}
      data-slot="table-body"
      {...props}
    />
  );
}

const interactiveRowClasses =
  "cursor-pointer focus-visible:outline-2 focus-visible:outline-ring focus-visible:-outline-offset-2";

/**
 * Controls that own their own activation, so a click echoing out of one must
 * not also fire the row.
 */
const CELL_CONTROL_SELECTOR =
  "a,button,input,select,textarea,label,[role='button'],[role='checkbox'],[role='link'],[role='menuitem']";

/**
 * Whether a click should activate the row.
 *
 * Nested controls own both pointer and keyboard activation. Everything else in
 * the row activates it, including pointerless clicks from assistive technology
 * or `element.click()` on a plain cell.
 */
function isRowActivationClick(event: React.MouseEvent<HTMLTableRowElement>): boolean {
  const target = event.target;
  if (!(target instanceof Element)) {
    return true;
  }
  return target.closest(CELL_CONTROL_SELECTOR) === null;
}

/**
 * Whether a key event on the row itself should activate it.
 *
 * Claims the keystroke when it does, because Space would otherwise scroll the
 * page out from under the row that just handled it.
 */
function consumesRowActivationKey(event: React.KeyboardEvent<HTMLTableRowElement>): boolean {
  if (event.defaultPrevented || event.target !== event.currentTarget) {
    return false;
  }
  if (event.key !== "Enter" && event.key !== " ") {
    return false;
  }
  event.preventDefault();
  return true;
}

/**
 * Builds a row event handler that forwards to the caller first, then activates
 * the row when `activates` accepts the event. Shared by the click and keydown
 * paths so both stay in step.
 */
function createRowActivationHandler<E>({
  activates,
  forward,
  onActivate,
}: {
  activates: (event: E) => boolean;
  forward?: (event: E) => void;
  onActivate?: () => void;
}) {
  return (event: E) => {
    forward?.(event);
    if (onActivate && activates(event)) {
      onActivate();
    }
  };
}

type TableRowProps = React.ComponentProps<"tr"> & {
  /**
   * Marks the row as its own primary control and wires click plus Enter/Space.
   *
   * The row stays a native `tr` rather than taking `role="button"`, so screen
   * readers keep row and cell semantics while the row becomes reachable by
   * keyboard.
   *
   * Key events raised inside a cell are ignored, and so are the clicks the
   * browser synthesizes from them, so keyboard activation of a nested control
   * never also fires the row. A real *pointer* click on a nested control still
   * bubbles — those cells must call `stopPropagation` themselves.
   *
   * Pass `undefined` when the row is not clickable; the row then stays
   * unfocusable and renders exactly as before. A caller-supplied `tabIndex`
   * still wins, so a long table can adopt a roving-tabindex pattern without
   * giving up row activation.
   */
  onActivate?: () => void;
};

function TableRow({
  className,
  onActivate,
  onClick,
  onKeyDown,
  tabIndex,
  ...props
}: TableRowProps) {
  return (
    <tr
      {...props}
      className={cn(
        "border-b transition-colors duration-200 hover:bg-muted/50 data-[state=selected]:bg-muted",
        onActivate && interactiveRowClasses,
        className,
      )}
      data-slot="table-row"
      onClick={createRowActivationHandler({
        activates: isRowActivationClick,
        forward: onClick,
        onActivate,
      })}
      onKeyDown={createRowActivationHandler({
        activates: consumesRowActivationKey,
        forward: onKeyDown,
        onActivate,
      })}
      tabIndex={onActivate ? (tabIndex ?? 0) : tabIndex}
    />
  );
}

type NumericCellProps<Element extends "th" | "td"> = React.ComponentProps<Element> & {
  numeric?: boolean;
};

const numericClasses = "text-right text-numeric";

function TableHead({ className, numeric, ...props }: NumericCellProps<"th">) {
  return (
    <th
      className={cn(
        "h-10 whitespace-nowrap px-2 text-left align-middle font-medium text-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        numeric && numericClasses,
        className,
      )}
      data-slot="table-head"
      {...props}
    />
  );
}

function TableCell({ className, numeric, ...props }: NumericCellProps<"td">) {
  return (
    <td
      className={cn(
        "whitespace-nowrap p-2 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        numeric && numericClasses,
        className,
      )}
      data-slot="table-cell"
      {...props}
    />
  );
}

export { Table, TableBody, TableCell, TableHead, TableHeader, TableRow };
